import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { UserAuth } from './AuthContext';
import { requiresVerification, VERIFY_EMAIL_PATH } from './emailVerification.js';
import { isSuperUserAllowedPath } from '../utils/rbacUtils.js';
import { SUPER_USER_LANDING } from '../config/accessControl.js';
import { useHeldRefusal } from './accessHold.js';
import { loginUrl } from './sessionExpiry.js';

const BYPASS_PATHS = ['/onboarding', '/signup', '/demo'];

export const ProtectedRoute = ({ children }) => {
    const { user, authResolved } = UserAuth();
    const location = useLocation();
    const userId = useSelector(state => state.userDetails.userId);
    const orgs   = useSelector(state => state.userDetails.orgs);
    const superUser = useSelector(state => state.userDetails.superUser);
    const userDetailsLoaded = useSelector(state => state.userDetails.userDetailsLoaded);
    const selectedOrgId = useSelector(state => state.dashboardContext.selectedOrgId);

    // Worked out before any return (hooks): the active org suspended / archived while this page was open is held -
    // the banner says so - rather than the page being swapped for /not-authorized under the user's unsaved work.
    const activeOrg = orgs && orgs.length > 0 ? (selectedOrgId ? orgs.find(o => o.orgId === selectedOrgId) : orgs[0]) : null;
    const orgClosed = !!(userId && userDetailsLoaded && !superUser && activeOrg
        && (activeOrg.orgState === 'SUSPENDED' || activeOrg.orgState === 'ARCHIVED'));
    const keepPage = useHeldRefusal(location.pathname, orgClosed,
        activeOrg?.orgState === 'ARCHIVED'
            ? 'Your access to this organization was removed.'
            : 'Your access to this organization was suspended.');

    if (!user) {
        // Firebase may still be restoring this tab's session: wait for its answer rather than send a signed-in user
        // away. Signed out: the sign-in page, which brings them back to this page afterwards.
        if (!authResolved) return null;
        return <Navigate to={loginUrl(`${location.pathname}${location.search}${location.hash}`)} replace />;
    }

    // Before BYPASS_PATHS: /onboarding and /signup are where an unverified session would otherwise
    // create an org and a user record, and stop looking unverified.
    //
    // Keyed on the CREDENTIAL, not on the flag alone: only an email/password account has a
    // verification mail to open. A Google or Microsoft session was proved by the provider, and
    // Firebase may leave emailVerified false for Microsoft regardless — gating those here trapped
    // them in a redirect loop they could not escape. See requiresVerification.
    if (requiresVerification(user)) {
        return <Navigate to={VERIFY_EMAIL_PATH} replace state={{ from: `${location.pathname}${location.search}` }} />;
    }

    if (BYPASS_PATHS.some(p => location.pathname.startsWith(p))) {
        return children;
    }

    // After login, if we know the user has no orgs yet, send them to onboarding — EXCEPT platform
    // super-users (control plane, intentionally no org). Only decide once fresh user data has loaded
    // this session: superUser is not persisted, so on a hard refresh we must let the layout re-fetch
    // it (rendering children) before bouncing a no-org super-user to onboarding.
    if (userId && userDetailsLoaded && !superUser && (!orgs || orgs.length === 0)) {
        const accessPending = localStorage.getItem('gp_access_request_pending');
        return <Navigate to={accessPending ? '/demo' : '/onboarding'} replace />;
    }

    // Org-membership suspension gate. A member whose active org is SUSPENDED/ARCHIVED can still
    // authenticate, but the PDP denies all their access — so a dashboard would just show the generic
    // "no game access" empty state. Route them to /not-authorized, which renders the explicit "Access
    // Suspended" card (and offers switching to another active org). Super-users are control-plane and
    // never org-suspended. This lives in ProtectedRoute (not a specific layout) because different
    // dashboard sections use different layouts (DashboardLayout vs GameDecisionCenterLayout) — they all
    // share this wrapper. /not-authorized is outside ProtectedRoute, so there's no redirect loop.
    if (userId && userDetailsLoaded && !superUser && orgs && orgs.length > 0) {
        const currentOrg = selectedOrgId ? orgs.find(o => o.orgId === selectedOrgId) : orgs[0];
        if (currentOrg && (currentOrg.orgState === 'SUSPENDED' || currentOrg.orgState === 'ARCHIVED') && !keepPage) {
            return <Navigate to="/not-authorized" replace />;
        }
    }

    // Super User is a limited control-plane role: confine it to its allowed surfaces (Cheats,
    // account/system pages, and the read-only Game Registration view). Anything else — including
    // operational pages reached by direct URL — bounces to the games view. Gated on
    // userDetailsLoaded because `superUser` isn't persisted (false during the hard-refresh window),
    // so we never wrongly bounce a real super user before their role resolves.
    if (superUser && userDetailsLoaded && !isSuperUserAllowedPath(location.pathname)) {
        return <Navigate to={SUPER_USER_LANDING} replace />;
    }

    return children;
};
